import client from './client';

/**
 * Auth API Service
 * Handles all authentication-related API calls
 */

/**
 * Sign up a new user
 * @param {Object} data - Signup data
 * @param {string} data.email - User email
 * @param {string} data.name - User full name
 * @param {string} [data.organization] - Organization name (optional)
 * @returns {Promise<Object>} Response with message
 */
export const signup = async (data) => {
  const response = await client.post('/auth/signup', data);
  return response.data;
};

/**
 * Verify email with token
 * @param {string} token - Verification token from email
 * @returns {Promise<Object>} Response with access_token and user data
 */
export const verifyEmail = async (token) => {
  const response = await client.get('/auth/verify-email', {
    params: { token },
  });
  return response.data;
};

/**
 * Request magic link for login
 * @param {string} email - User email
 * @returns {Promise<Object>} Response with message
 */
export const requestMagicLink = async (email) => {
  const response = await client.post('/auth/magic-link', { email });
  return response.data;
};

/**
 * Verify magic link token
 * @param {string} token - Magic link token
 * @returns {Promise<Object>} Response with access_token and user data
 */
export const verifyMagicLink = async (token) => {
  const response = await client.get('/auth/verify', {
    params: { token },
  });
  return response.data;
};

/**
 * Get current user session
 * @returns {Promise<Object>} User data with subscription and credits
 */
export const getMe = async () => {
  const response = await client.get('/user/me');
  return response.data;
};
/**
 * Logout current user
 * @returns {Promise<Object>} Response with message
 */
export const logout = async () => {
  const response = await client.post('/auth/logout');
  return response.data;
};

/**
 * Resend verification email
 * @param {string} email - User email
 * @returns {Promise<Object>} Response with message
 */
export const resendVerification = async (email) => {
  const response = await client.post('/auth/resend-verification', { email });
  return response.data;
};

/**
 * Submit email to waitlist via Google Apps Script → Google Sheet.
 * Uses raw fetch (not axios client) because this targets a third-party URL.
 * @param {string} email - User email
 * @returns {Promise<void>}
 */
export const submitWaitlistEmail = async (email) => {
  const url = import.meta.env.VITE_GOOGLE_SHEETS_WAITLIST_URL;

  if (!url) {
    console.warn('[Waitlist] VITE_GOOGLE_SHEETS_WAITLIST_URL is not set — skipping submission.');
    return;
  }

  const params = new URLSearchParams({
    email,
    timestamp: new Date().toISOString(),
  });

  await fetch(`${url}?${params}`, { mode: 'no-cors' });
};

const authApi = {
  signup,
  verifyEmail,
  requestMagicLink,
  verifyMagicLink,
  getMe,
  logout,
  resendVerification,
  submitWaitlistEmail,
};

export default authApi;
